import { useMemo } from "react";
import { useFieldConfiguration } from "./useFieldConfiguration";
import { FieldDisplayType } from "../types";
import { FieldFormData, OptionsManagerData } from "../utils/fieldFormUtils";
import { APIFieldItem } from "@/pages/settings/types";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { getLookupFieldConfig } from "../lookupFieldRegistry";

interface UseFieldConfigurationDataProps {
  fieldType: FieldDisplayType | null;
  formData: FieldFormData;
  optionsManagerData: OptionsManagerData;
  fieldData?: APIFieldItem | null;
  formSetValue: any;
  onOptionsManagerChange: (data: OptionsManagerData) => void;
  onHiddenToggle?: (id: number | string) => void;
  isPresetFieldEdit?: boolean;
  isEditableSystemField?: boolean;
}

interface FieldConfigurationData {
  configSection: FormSectionProps | null;
  configFields: any[];
  bottomCustomComponent: React.ReactNode | undefined;
}

export const useFieldConfigurationData = ({
  fieldType,
  formData,
  optionsManagerData,
  fieldData,
  formSetValue,
  onOptionsManagerChange,
  onHiddenToggle,
  isPresetFieldEdit = false,
  isEditableSystemField = false
}: UseFieldConfigurationDataProps): FieldConfigurationData => {
  const { renderFieldConfiguration } = useFieldConfiguration();

  const configurationData = useMemo(() => {
    if (!fieldType) {
      return {
        configSection: null,
        configFields: [],
        bottomCustomComponent: undefined
      };
    }

    const configFormData = {
      ...formData,
      ...optionsManagerData,
      option1: fieldData?.config?.option1 || formData?.option1 || "",
      option2: fieldData?.config?.option2 || formData?.option2 || "",
      minValue:
        formData?.minValue !== undefined
          ? formData.minValue
          : fieldData?.config?.minValue,
      maxValue:
        formData?.maxValue !== undefined
          ? formData.maxValue
          : fieldData?.config?.maxValue,
      defaultValue:
        optionsManagerData?.defaultValue ||
        fieldData?.config?.defaultValue ||
        (fieldType === FieldDisplayType.CURRENCY
          ? ""
          : fieldType === FieldDisplayType.CHECKBOX
            ? "unchecked"
            : fieldType === FieldDisplayType.SWITCH
              ? "option1"
              : "none")
    };

    // Lookup-backed fields always use the registry panel (flag-independent); the
    // flag only layers on hide/show, sort, and rename confirmation elsewhere.
    const lookupFieldKey =
      isEditableSystemField && getLookupFieldConfig(fieldData?.fieldKey)
        ? fieldData?.fieldKey
        : undefined;

    const configSection = renderFieldConfiguration({
      fieldType,
      formData: configFormData,
      setValue:
        formSetValue && typeof formSetValue === "function"
          ? formSetValue
          : () => {},
      watch: (() => formData) as any,
      onOptionsManagerChange,
      onHiddenToggle,
      isPresetFieldEdit: isPresetFieldEdit && !isEditableSystemField,
      isEditableSystemField,
      lookupFieldKey
    });

    return {
      configSection,
      configFields: configSection?.fields || [],
      bottomCustomComponent: configSection?.bottomCustomComponent
    };
  }, [
    fieldType,
    formData,
    optionsManagerData,
    fieldData,
    formSetValue,
    onOptionsManagerChange,
    onHiddenToggle,
    isPresetFieldEdit,
    isEditableSystemField,
    renderFieldConfiguration
  ]);

  return configurationData;
};
